import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './i18n'; // Initialize i18n
import '@/App.css';

// Import pages
import FrontPage from './pages/FrontPage';
import ErrorBoundary from './components/ErrorBoundary';
import RequireAuth from './components/RequireAuth';
import { Toaster } from './components/ui/sonner';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import BusinessSignUp from './pages/BusinessSignUp';
import HelperLogin from './pages/HelperLogin';
import HelperDashboard from './pages/HelperDashboard';
import AffiliateProgram from './pages/AffiliateProgram';
import TownAmbassadorPoster from './pages/TownAmbassadorPoster';
import AffiliateOnly from './components/AffiliateOnly';
import AdminStripeRollout from './pages/AdminStripeRollout';
import AdminBusinessAudit from './pages/AdminBusinessAudit';
import Discover from './pages/Discover';
import FollowedTowns from './pages/FollowedTowns';
import Favorites from './pages/Favorites';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import Offers from './pages/Offers';
import OfferDetail from './pages/OfferDetail';
import SafetyDetail from './pages/SafetyDetail';
import DeepLinkRedirector from './components/DeepLinkRedirector';
import Profile from './pages/Profile';
import CalendarPage from './pages/CalendarPage';
import PricePage from './pages/PricePage';
import LocalPrices from './pages/LocalPrices';
import NotificationPreferences from './pages/NotificationPreferences';
import RestoreAccount from './pages/RestoreAccount';
import VerifyBackupEmail from './pages/VerifyBackupEmail';
import MyReviews from './pages/MyReviews';
import ReviewGuidelines from './pages/ReviewGuidelines';
import BusinessGuide from './pages/BusinessGuide';
import HelpGuide from './pages/HelpGuide';
import Settings from './pages/Settings';
import NotificationInbox from './pages/NotificationInbox';
import MobileDashboard from './pages/MobileDashboard';import AdminTranslations from './pages/AdminTranslations';
import SwapThread from './pages/SwapThread';
import DataDeletion from './pages/DataDeletion';
import Emergency from './pages/Emergency';
import Holidays from './pages/Holidays';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import DisputePolicy from './pages/DisputePolicy';
import BusinessDetail from './pages/BusinessDetail';
import BusinessDashboard from './pages/BusinessDashboard';
import ChangePassword from './pages/ChangePassword';
import ClaimYourBusiness from './pages/ClaimYourBusiness';
import CharityApplication from './pages/CharityApplication';
import { OfflineBanner } from './components/OfflineBanner';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { NotificationProvider } from './contexts/NotificationContext';
import { PWAInstallProvider } from './contexts/PWAInstallContext';
import PWAInstallBanner from './components/PWAInstallBanner';
import AuthorityWelcomeToast from './components/AuthorityWelcomeToast';
import RegionVillagePicker from './components/RegionVillagePicker';
import TownPill from './components/TownPill';

// Admin pages
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AuthorityOnboardingGuide from './pages/AuthorityOnboardingGuide';
import AuthorityOnboardingRoute from './pages/AuthorityOnboardingRoute';
import AuthorityScheduledAlerts from './pages/AuthorityScheduledAlerts';
import AdminPricingConfig from './pages/AdminPricingConfig';
import AdminRemovalRequests from './pages/AdminRemovalRequests';
import AdminBackupRecovery from './pages/AdminBackupRecovery';
import AdminBusinessDetail from './pages/AdminBusinessDetail';
import AdminSafetyBulletin from './pages/AdminSafetyBulletin';
import VerifyEmail from './pages/VerifyEmail';

// Local Safety Bulletin pages
import SafetyBulletin from './pages/SafetyBulletin';
import IncidentRoom from './pages/IncidentRoom';
import SafetyBulletinSubmit from './pages/SafetyBulletinSubmit';
import SafetyBulletinMine from './pages/SafetyBulletinMine';

// Payment pages
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import BoostSuccess from './pages/BoostSuccess';

// Classifieds pages (Feb 2026 — renamed Marketplace but old routes preserved)
import Classifieds from './pages/Classifieds';
import ClassifiedDetail from './pages/ClassifiedDetail';
import PostClassified from './pages/PostClassified';
import UnsubscribeAlert from './pages/UnsubscribeAlert';
import PostLostFound from './pages/PostLostFound';
import LostFoundDetail from './pages/LostFoundDetail';
import LostFound from './pages/LostFound';

// Always call the same origin the page was loaded from. This eliminates
// cross-origin issues when the app is served from multiple hostnames
// (e.g. apex + www, or preview + custom domain) where the JS bundle would
// otherwise be locked to a single REACT_APP_BACKEND_URL baked in at build
// time. The backend is mounted under /api on every domain that serves the
// frontend, so relative routing is always correct.
//   - Browser context  → use window.location.origin (works on apex, www, preview)
//   - Non-browser fallback (SSR / tests) → use REACT_APP_BACKEND_URL if set
const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const BACKEND_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : ENV_BACKEND;
export const API = `${BACKEND_URL}/api`;

const RTL_LANGUAGES = ['ar'];

function RTLDirectionManager() {
  const { i18n } = useTranslation();
  useEffect(() => {
    const isRTL = RTL_LANGUAGES.includes(i18n.language);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  return null;
}

// Axios instance with auth
export const axiosInstance = axios.create({
  baseURL: API,
});

// Auth Context
export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// --- Service-worker auth-token bridge -------------------------------------
// Used so the SW can attach a JWT to failure-report telemetry. Lives in SW
// memory only — never persisted, cleared on logout.
function _setSwAuthToken(token) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const send = (controller) => controller && controller.postMessage({ type: 'SET_AUTH_TOKEN', token });
  if (navigator.serviceWorker.controller) {
    send(navigator.serviceWorker.controller);
  } else {
    navigator.serviceWorker.ready.then((reg) => send(reg.active)).catch(() => {});
  }
}
function _clearSwAuthToken() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage('CLEAR_AUTH_TOKEN');
  }
}
// --------------------------------------------------------------------------

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check both localStorage (remember me) and sessionStorage (session only)
    const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
    const token = storage.getItem('token');
    const role = storage.getItem('role');
    const userId = storage.getItem('userId');

    if (token && role && userId) {
      setUser({ token, role, userId });
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Hand the SW our JWT so it can attach it to anonymous failure reports
      // (only used to identify which user a failed submission came from —
      // privacy-by-design: lives in SW memory only, cleared on logout).
      _setSwAuthToken(token);
      // Proactively sync role from DB in case it changed (e.g. claim approved)
      // AND slide the session forward: always persist the freshly-minted token so
      // "remember me" actually keeps users signed in (the server returns a new
      // full-length token on every load — discarding it let sessions silently
      // expire once the original token aged out).
      axiosInstance.get('/auth/refresh-session').then(res => {
        const newRole = res.data.role;
        const freshToken = res.data.access_token;
        if (freshToken) {
          storage.setItem('token', freshToken);
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${freshToken}`;
          _setSwAuthToken(freshToken);
        }
        if (newRole && newRole !== role) {
          storage.setItem('role', newRole);
          setUser(prev => ({ ...prev, token: freshToken || prev.token, role: newRole }));
        } else if (freshToken) {
          setUser(prev => ({ ...prev, token: freshToken }));
        }
      }).catch(() => {});
    }
    setLoading(false);
  }, []);

  const login = (token, role, userId, rememberMe = true) => {
    // Clear both storages first to prevent stale data
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('role');
    sessionStorage.removeItem('userId');

    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('token', token);
    storage.setItem('role', role);
    storage.setItem('userId', userId);
    setUser({ token, role, userId });
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    _setSwAuthToken(token);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('role');
    sessionStorage.removeItem('userId');
    setUser(null);
    delete axiosInstance.defaults.headers.common['Authorization'];
    _clearSwAuthToken();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-purple-500 to-purple-700">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <NotificationProvider>
      <PWAInstallProvider>
      <RTLDirectionManager />
      <BrowserRouter>
        <ErrorBoundary>
        <Toaster position="top-center" richColors closeButton mobileOffset={{ top: '96px' }} />
        <AuthorityWelcomeToast />
        <DeepLinkRedirector />
        <Routes>
          {/* Public routes */}
          {/* Dashboard is now the app's landing page. The old FrontPage is
              preserved (not deleted) at /welcome and /old-home. */}
          <Route path="/" element={<MobileDashboard />} />
          <Route path="/welcome" element={<FrontPage />} />
          <Route path="/old-home" element={<FrontPage />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/business/signup" element={<BusinessSignUp />} />
          <Route path="/business/:id" element={<BusinessDetail />} />
          <Route path="/claim-your-business" element={<ClaimYourBusiness />} />
          <Route path="/charity/apply" element={<CharityApplication />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancelled" element={<PaymentCancelled />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/review-guidelines" element={<ReviewGuidelines />} />
          <Route path="/business-guide" element={<BusinessGuide />} />
          <Route path="/help" element={<HelpGuide />} />
          <Route path="/helper/login" element={<AffiliateOnly><HelperLogin /></AffiliateOnly>} />
          <Route path="/helper/dashboard" element={<AffiliateOnly><HelperDashboard /></AffiliateOnly>} />
          <Route path="/affiliate" element={<AffiliateOnly><AffiliateProgram /></AffiliateOnly>} />
          <Route path="/earn" element={<AffiliateOnly><AffiliateProgram /></AffiliateOnly>} />
          <Route path="/partners/poster" element={<AffiliateOnly><TownAmbassadorPoster /></AffiliateOnly>} />
          <Route path="/admin/stripe-rollout" element={<AdminStripeRollout />} />

          {/* Public browsing routes */}
          <Route path="/explore" element={<Discover />} />
          <Route path="/price" element={<PricePage />} />
          <Route path="/local" element={<LocalPrices />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/home" element={<MobileDashboard />} />
          <Route path="/swap/:offerId" element={<SwapThread />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/offers/:id" element={<OfferDetail />} />
          <Route path="/safety/:id" element={<SafetyDetail />} />
          <Route path="/classifieds" element={<Classifieds />} />
          <Route path="/marketplace" element={<Classifieds />} />
          <Route path="/classifieds/post" element={<PostClassified />} />
          <Route path="/marketplace/post" element={<PostClassified />} />
          <Route path="/classifieds/unsubscribe/:searchId" element={<UnsubscribeAlert />} />
          <Route path="/classifieds/:id" element={<ClassifiedDetail />} />
          <Route path="/marketplace/:id" element={<ClassifiedDetail />} />
          <Route path="/lost-found" element={<LostFound />} />
          <Route path="/lost-found/post" element={<PostLostFound />} />
          <Route path="/lost-found/:id" element={<LostFoundDetail />} />

          {/* Public route — anonymous users see their device-bound favourites */}
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/preferences" element={<NotificationPreferences />} />
          <Route path="/restore" element={<RestoreAccount />} />
          <Route path="/verify-backup-email" element={<VerifyBackupEmail />} />
          <Route
            path="/profile"
            element={<RequireAuth><Profile /></RequireAuth>}
          />
          <Route
            path="/calendar"
            element={<RequireAuth><CalendarPage /></RequireAuth>}
          />
          <Route
            path="/my-reviews"
            element={<RequireAuth><MyReviews /></RequireAuth>}
          />
          <Route
            path="/change-password"
            element={<RequireAuth><ChangePassword /></RequireAuth>}
          />
          <Route
            path="/settings"
            element={<RequireAuth><Settings /></RequireAuth>}
          />
          <Route path="/notifications" element={<NotificationInbox />} />
          <Route path="/followed-towns" element={<FollowedTowns />} />
          <Route path="/admin/translations" element={<AdminTranslations />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/emergency" element={<Emergency />} />
          <Route path="/holidays" element={<Holidays />} />
          <Route path="/safety-bulletin" element={<SafetyBulletin />} />
          <Route path="/community-safety" element={<SafetyBulletin />} />
          <Route path="/community-hub" element={<SafetyBulletin />} />
          <Route path="/incidents/:id" element={<IncidentRoom />} />
          <Route
            path="/safety-bulletin/submit"
            element={<RequireAuth><SafetyBulletinSubmit /></RequireAuth>}
          />
          <Route
            path="/safety-bulletin/mine"
            element={<RequireAuth><SafetyBulletinMine /></RequireAuth>}
          />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/disputes" element={<DisputePolicy />} />
          <Route
            path="/business/dashboard"
            element={<RequireAuth condition={(u) => !!u && u.role === 'business'}><BusinessDashboard /></RequireAuth>}
          />

          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/business-audit"
            element={
              user && user.role === 'admin' ? (
                <AdminBusinessAudit />
              ) : (
                <Navigate to="/admin/login" />
              )
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              user && user.role === 'admin' ? (
                <AdminDashboard />
              ) : (
                <Navigate to="/admin/login" />
              )
            }
          />
          <Route
            path="/admin/business/:id"
            element={<AdminBusinessDetail />}
          />
          <Route
            path="/admin/safety-bulletin"
            element={
              user && user.role === 'admin' ? (
                <AdminSafetyBulletin />
              ) : (
                <Navigate to="/admin/login" />
              )
            }
          />
          <Route
            path="/admin/removal-requests"
            element={
              user && user.role === 'admin' ? (
                <AdminRemovalRequests />
              ) : (
                <Navigate to="/admin/login" />
              )
            }
          />
          <Route
            path="/admin/authority-onboarding"
            element={<AuthorityOnboardingRoute />}
          />
          <Route
            path="/authority/scheduled"
            element={user ? <AuthorityScheduledAlerts /> : <Navigate to="/signin" />}
          />
          <Route
            path="/admin/pricing-config"
            element={
              user && user.role === 'admin' ? (
                <AdminPricingConfig />
              ) : (
                <Navigate to="/admin/login" />
              )
            }
          />
          <Route
            path="/admin/backup-recovery"
            element={
              user && user.role === 'admin' ? (
                <AdminBackupRecovery />
              ) : (
                <Navigate to="/admin/login" />
              )
            }
          />

          {/* Redirect unknown routes */}
          <Route path="/boost/success" element={<BoostSuccess />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </ErrorBoundary>
        <OfflineBanner />
        <PWAInstallBanner />
        <RegionVillagePicker />
        <TownPill />
      </BrowserRouter>
      </PWAInstallProvider>
      </NotificationProvider>
    </AuthContext.Provider>
  );
}

export default App;
